<template>
    <div id="telnyx-settings">
        <div class="page-header">
            <div class="left">
                <h3>Telnyx Ayarları</h3>
                <p>Telnyx kanalınızın bağlantı ayarlarını düzenleyin</p>
            </div>
            <div class="right">
                <a-button type="primary" @click="testConnection" :loading="testing"> Bağlantıyı Test Et </a-button>
            </div>
        </div>

        <div class="settings-content">
            <!-- Channel Information -->
            <div class="settings-section">
                <div class="section-header">
                    <h4>Kanal Bilgileri</h4>
                    <p>Kanal adı ve açıklama bilgileri</p>
                </div>
                <div class="section-content">
                    <a-form layout="vertical">
                        <a-form-item label="Kanal Adı">
                            <a-input
                                v-model="localChannel.name"
                                placeholder="Kanal adını girin"
                                @change="handleChange"
                            />
                        </a-form-item>
                    </a-form>
                </div>
            </div>

            <!-- Telnyx Account Settings -->
            <div class="settings-section">
                <div class="section-header">
                    <h4>Telnyx Hesap Bilgileri</h4>
                    <p>Telnyx hesabınızın giriş bilgileri</p>
                </div>
                <div class="section-content">
                    <a-form layout="vertical">
                        <a-row :gutter="16">
                            <a-col :span="12">
                                <a-form-item label="Username">
                                    <a-input
                                        v-model="localChannel.payload.username"
                                        placeholder="Telnyx kullanıcı adınız"
                                        @change="handleChange"
                                    />
                                </a-form-item>
                            </a-col>
                            <a-col :span="12">
                                <a-form-item label="Password">
                                    <a-input-password
                                        v-model="localChannel.payload.password"
                                        placeholder="Telnyx şifreniz"
                                        @change="handleChange"
                                    />
                                </a-form-item>
                            </a-col>
                        </a-row>
                        <a-form-item label="Caller Phone Number">
                            <a-input
                                v-model="localChannel.payload.callerPhoneNumber"
                                placeholder="Örn: +18445420900"
                                @change="handleChange"
                            />
                        </a-form-item>
                    </a-form>
                </div>
            </div>

            <!-- Webhook Settings -->
            <div class="settings-section">
                <div class="section-header">
                    <h4>Webhook Ayarları</h4>
                    <p>Telnyx panelinde webhook URL'sini yapılandırın</p>
                </div>
                <div class="section-content">
                    <a-alert message="Webhook URL" type="info" show-icon>
                        <div slot="description">
                            <div class="webhook-info">
                                <span class="webhook-url">{{ getWebhookAddress() }}</span>
                                <a-button 
                                    type="link" 
                                    size="small" 
                                    icon="copy" 
                                    @click="copyWebhookAddress"
                                    style="padding: 0; margin-left: 8px;"
                                >
                                    Kopyala
                                </a-button>
                            </div>
                            <div class="webhook-description">
                                Bu URL'yi Telnyx panelinizdeki webhook ayarlarında kullanın. 
                                Telnyx'den gelen call event'leri bu endpoint'e yönlendirilecek.
                            </div>
                        </div>
                    </a-alert>
                </div>
            </div>

            <!-- Connection Settings -->
            <div class="settings-section">
                <div class="section-header">
                    <h4>Bağlantı Ayarları</h4>
                    <p>SIP sunucu bağlantı bilgileri</p>
                </div>
                <div class="section-content">
                    <a-form layout="vertical">
                        <a-form-item label="Domain">
                            <a-input
                                v-model="localChannel.payload.domain"
                                placeholder="sip.telnyx.com"
                                @change="handleChange"
                            />
                        </a-form-item>
                        <a-form-item label="WebSocket Server">
                            <a-input
                                v-model="localChannel.payload.websocketServer"
                                placeholder="wss://sip.telnyx.com:7443"
                                @change="handleChange"
                            />
                        </a-form-item>
                        <a-form-item label="API Key">
                            <a-input-password
                                v-model="localChannel.payload.apiKey"
                                placeholder="API Key"
                                @change="handleChange"
                            />
                        </a-form-item>
                    </a-form>
                </div>
            </div>

            <!-- Status Information -->
            <div class="settings-section">
                <div class="section-header">
                    <h4>Durum Bilgileri</h4>
                    <p>Kanalın mevcut durumu</p>
                </div>
                <div class="section-content">
                    <div class="status-grid">
                        <div class="status-item">
                            <div class="status-label">Kanal Durumu</div>
                            <div class="status-value">
                                <a-tag :color="value.enabled ? 'green' : 'red'">
                                    {{ value.enabled ? "Aktif" : "Pasif" }}
                                </a-tag>
                            </div>
                        </div>
                        <div class="status-item">
                            <div class="status-label">Oluşturma Tarihi</div>
                            <div class="status-value">
                                {{ $moment(value.createdAt).format("DD.MM.YYYY HH:mm") }}
                            </div>
                        </div>
                        <div class="status-item">
                            <div class="status-label">Son Güncelleme</div>
                            <div class="status-value">
                                {{ $moment(value.updatedAt).format("DD.MM.YYYY HH:mm") }}
                            </div>
                        </div>
                        <div class="status-item">
                            <div class="status-label">Kanal ID</div>
                            <div class="status-value channel-id">
                                <span>{{ value._id }}</span>
                                <a-button type="link" size="small" @click="copyChannelId" icon="copy">
                                    Kopyala
                                </a-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Danger Zone -->
            <div class="settings-section danger-zone">
                <div class="section-header">
                    <h4>Tehlikeli Alan</h4>
                    <p>Bu işlemler geri alınamaz</p>
                </div>
                <div class="section-content">
                    <div class="danger-actions">
                        <div class="danger-item">
                            <div class="danger-info">
                                <div class="danger-title">Kanalı Devre Dışı Bırak</div>
                                <div class="danger-desc">Kanal geçici olarak devre dışı bırakılır</div>
                            </div>
                            <a-button
                                :type="value.enabled ? 'danger' : 'primary'"
                                @click="toggleChannelStatus"
                                :loading="toggleLoading"
                            >
                                {{ value.enabled ? "Devre Dışı Bırak" : "Aktif Et" }}
                            </a-button>
                        </div>
                        <div class="danger-item">
                            <div class="danger-info">
                                <div class="danger-title">Kanalı Sil</div>
                                <div class="danger-desc">Kanal kalıcı olarak silinir</div>
                            </div>
                            <a-button type="danger" @click="$router.push('/integrations/webphone/telnyx')">
                                Kanalı Sil
                            </a-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import log2 from "../../../../../utils/log2";
import CONFIG from "../../../../../config/config";
import { copyText2Clipboard } from "../../../../../utils/utils";

const log = log2("TelnyxSettings");

export default Vue.extend({
    name: "TelnyxSettings",
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            localChannel: _.cloneDeep(this.value),
            testing: false,
            toggleLoading: false,
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site?._id,
        }),
    },
    methods: {
        ...mapActions({
            updateChannel: "channel/updateChannel",
        }),
        getWebhookAddress() {
            return `${CONFIG.SOCKET_URL}/webhook/webphone/telnyx?siteId=${this.siteId}&channelId=${this.value._id}`;
        },
        copyWebhookAddress() {
            copyText2Clipboard(this.getWebhookAddress());
            this.$message.success("Webhook URL kopyalandı");
        },
        handleChange: _.debounce(function () {
            this.saveSettings();
        }, 1000),
        async saveSettings() {
            try {
                await this.updateChannel({
                    _id: this.localChannel._id,
                    name: this.localChannel.name,
                    enabled: this.localChannel.enabled,
                    payload: this.localChannel.payload,
                    visibility: this.localChannel.visibility,
                    forward: this.localChannel.forward,
                    commentForward: this.localChannel.commentForward,
                    zohoForward: this.localChannel.zohoForward,
                });

                this.$emit("input", this.localChannel);
                this.$message.success("Ayarlar başarıyla kaydedildi");
                log("Settings saved:", this.localChannel);
            } catch (error) {
                console.error("Settings save error:", error);
                this.$message.error("Ayarlar kaydedilirken hata oluştu");
            }
        },
        async testConnection() {
            try {
                this.testing = true;
                // Burada gerçek bir bağlantı testi yapılabilir
                await new Promise((resolve) => setTimeout(resolve, 2000));
                this.$message.success("Bağlantı testi başarılı!");
            } catch (error) {
                console.error("Connection test error:", error);
                this.$message.error("Bağlantı testi başarısız!");
            } finally {
                this.testing = false;
            }
        },
        async toggleChannelStatus() {
            try {
                this.toggleLoading = true;
                this.localChannel.enabled = !this.localChannel.enabled;
                await this.saveSettings();
                this.$message.success(
                    `Kanal başarıyla ${this.localChannel.enabled ? "aktif edildi" : "devre dışı bırakıldı"}`
                );
            } catch (error) {
                console.error("Toggle status error:", error);
                this.$message.error("Kanal durumu değiştirilirken hata oluştu");
                this.localChannel.enabled = !this.localChannel.enabled; // Rollback
            } finally {
                this.toggleLoading = false;
            }
        },
        copyChannelId() {
            navigator.clipboard
                .writeText(this.value._id)
                .then(() => {
                    this.$message.success("Kanal ID kopyalandı");
                })
                .catch(() => {
                    this.$message.error("Kanal ID kopyalanamadı");
                });
        },
    },
    watch: {
        value: {
            handler(newVal) {
                this.localChannel = _.cloneDeep(newVal);
            },
            deep: true,
        },
    },
});
</script>

<style lang="scss" scoped>
#telnyx-settings {
    padding: 20px;
    background: var(--color-bg-default);
    height: 100%;

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 30px;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .left {
            h3 {
                margin: 0 0 5px 0;
                color: var(--color-text-primary);
                font-size: 20px;
                font-weight: 600;
            }

            p {
                margin: 0;
                color: var(--color-text-secondary);
                font-size: 14px;
            }
        }
    }

    .settings-content {
        .settings-section {
            background: white;
            border-radius: 12px;
            border: 1px solid var(--color-border-default);
            margin-bottom: 24px;
            overflow: hidden;

            &.danger-zone {
                border-color: #ff4d4f;

                .section-header {
                    background: #fff2f0;
                    border-bottom-color: #ffccc7;

                    h4 {
                        color: #cf1322;
                    }

                    p {
                        color: #cf1322;
                    }
                }
            }

            .section-header {
                padding: 20px 24px;
                background: #fafafa;
                border-bottom: 1px solid var(--color-border-default);

                h4 {
                    margin: 0 0 4px 0;
                    font-size: 16px;
                    font-weight: 600;
                    color: var(--color-text-primary);
                }

                p {
                    margin: 0;
                    font-size: 14px;
                    color: var(--color-text-secondary);
                }
            }

            .section-content {
                padding: 24px;
            }
        }

        .status-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 16px;

            .status-item {
                .status-label {
                    font-size: 12px;
                    font-weight: 500;
                    color: var(--color-text-secondary);
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 8px;
                }

                .status-value {
                    font-size: 14px;
                    font-weight: 500;
                    color: var(--color-text-primary);

                    &.channel-id {
                        display: flex;
                        align-items: center;
                        gap: 8px;

                        span {
                            font-family: monospace;
                            font-size: 12px;
                            word-break: break-all;
                        }
                    }
                }
            }
        }

        .danger-actions {
            .danger-item {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 16px 0;
                border-bottom: 1px solid #ffccc7;

                &:last-child {
                    border-bottom: none;
                }

                .danger-info {
                    .danger-title {
                        font-weight: 600;
                        color: #cf1322;
                        margin-bottom: 4px;
                    }

                    .danger-desc {
                        font-size: 14px;
                        color: #8c8c8c;
                    }
                }
            }
        }

        .webhook-info {
            display: flex;
            align-items: center;
            margin-bottom: 8px;

            .webhook-url {
                font-family: monospace;
                font-size: 12px;
                color: var(--color-text-primary);
                background: #f5f5f5;
                padding: 4px 8px;
                border-radius: 4px;
                word-break: break-all;
                flex: 1;
            }
        }

        .webhook-description {
            font-size: 12px;
            color: var(--color-text-secondary);
            line-height: 1.4;
        }
    }
}
</style>
